import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import PlanHeader from "../features/nutrition/nutrition-plan/components/plan-header";
import { Alert, Box, Button, Grid, Tab, Tabs, Typography } from "@mui/material";
import { createNutritionPlan, fetchActivePlan, selectActivePlan, selectIsLoading, updateNutritionPlan } from "../features/nutrition/nutrition-plan/nutrition-plan.slice";
import { useEffect, useState } from "react";
import { MealItemRequest, NutritionPlanRequest } from "../service/model/nutrition.model";
import Loader from "../components/loader";
import dayjs from "dayjs";
import { createMealItem } from "../features/nutrition/meal-items/meal-item.slice";
import MealItemForm from "../features/nutrition/meal-items/components/meal-item-form";
import { showNotification } from "../features/alerts/alert.slice";
import { NotificationType } from "../shared/enums";
import PlanBuilderActionButtons from "../features/nutrition/nutrition-plan/components/plan-builder-action-buttons";
import { getMember } from "../service/member.service";
import { Member } from "../service/model/member.model";
import { selectLoggedInUser } from "../features/auth/auth.slice";
import { isBusinessUser } from "../shared/utils";
import NutritionPlanDay from "../features/nutrition/nutrition-plan/components/nutrition-plan-day";

export type HeaderData = {
    startDate: string;
    endDate: string;
}

const NutritionPlanBuilder = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isLoading = useAppSelector(selectIsLoading);
    const activePlan = useAppSelector(selectActivePlan);
    const loggedInUser = useAppSelector(selectLoggedInUser);

    const { memberId } = useParams<{ memberId: string }>();
    const [showMealItemForm, setShowMealItemForm] = useState(false);
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [member, setMember] = useState<Member | null>(null);
    const [memberLoading, setMemberLoading] = useState(false);

    useEffect(() => {
        if (memberId) {
            dispatch(fetchActivePlan(Number(memberId)));
        }
    }, [memberId, dispatch]);

    useEffect(() => {
        if (activePlan?.cycleLength && selectedTab >= activePlan.cycleLength) {
            setSelectedTab(activePlan.cycleLength - 1);
        }
    }, [activePlan?.cycleLength, selectedTab]);

    useEffect(() => {
        let mounted = true;
        if (!memberId) {
            setMember(null);
            return;
        }
        setMemberLoading(true);
        getMember(Number(memberId))
            .then(m => {
                if (mounted) setMember(m);
            })
            .catch(err => {
                console.error("Failed to fetch member details:", err);
            })
            .finally(() => {
                if (mounted) setMemberLoading(false);
            });
        return () => { mounted = false; };
    }, [memberId]);

    const handleClose = () => {
        navigate(-1);
    };

    const handleMealItemSubmit = (data: MealItemRequest) => {
        dispatch(createMealItem({ ...data, })).then(resp => {
            if (resp.meta.requestStatus === 'fulfilled') {
                setShowMealItemForm(false);
            }
        });
    };

    const handleSave = async () => {
        if (!activePlan?.planSnapshot?.days || activePlan?.planSnapshot?.days?.length === 0) {
            dispatch(
                showNotification({
                    message: "At least one meal required. Please add meals to the plan before saving.",
                    type: NotificationType.Error,
                })
            );
            return;
        }
        if (!activePlan.cycleLength || activePlan.cycleLength < 1) {
            dispatch(
                showNotification({
                    message: "Cycle length must be at least 1. Please set a valid cycle length before saving.",
                    type: NotificationType.Error,
                })
            );
            return;
        }
        const dayWithNoMeals = activePlan.planSnapshot.days.find(day => !day.meals || day.meals.length === 0);
        if (dayWithNoMeals) {
            dispatch(
                showNotification({
                    message: `All days must have at least one meal. Day ${dayWithNoMeals.day} has no meals.`,
                    type: NotificationType.Error,
                })
            );
            return;
        }

        if (member) {
            const request: NutritionPlanRequest = {
                id: activePlan?.id,
                memberId: member.id,
                planSnapshot: {
                    days: activePlan?.planSnapshot?.days ?? [],
                    totalPlanNutrition: {
                        calories: activePlan?.planSnapshot?.totalPlanNutrition?.calories ?? 0,
                        protein: activePlan?.planSnapshot?.totalPlanNutrition?.protein ?? 0,
                        carbohydrates: activePlan?.planSnapshot?.totalPlanNutrition?.carbohydrates ?? 0,
                        fat: activePlan?.planSnapshot?.totalPlanNutrition?.fat ?? 0,
                        fiber: activePlan?.planSnapshot?.totalPlanNutrition?.fiber ?? 0,
                        sugar: activePlan?.planSnapshot?.totalPlanNutrition?.sugar ?? 0,
                    }
                },
                startDate: activePlan?.startDate ?? dayjs().format('YYYY-MM-DD'),
                endDate: activePlan?.endDate ?? dayjs().add(30, 'day').format('YYYY-MM-DD'),
                cycleLength: activePlan?.cycleLength ?? activePlan?.planSnapshot?.days?.length ?? 1,
            };
            if (activePlan?.id) {
                await dispatch(updateNutritionPlan(request));
            } else {
                await dispatch(createNutritionPlan(request));
            }
        }
    };

    return (
        <div>
            {(isLoading || memberLoading) ? (
                <Loader />
            ) : (
                <>
                    {member ? (
                        <>
                            <Typography variant="h5" gutterBottom>
                                Nutrition Plan
                            </Typography>

                            <Grid container direction="row" justifyContent="flex-end" spacing={2}>
                                <Button variant="contained" color="primary" onClick={() => setShowMealItemForm(true)} sx={{ mb: 2 }}>
                                    New Meal Item
                                </Button>
                                <Button variant="contained" color="primary" onClick={() => navigate("/meal-items")} sx={{ mb: 2 }}>
                                    Manage Meal Items
                                </Button>
                            </Grid>
                            {!activePlan?.id && isBusinessUser(loggedInUser?.user) && (
                                <Alert severity="info" variant="filled" sx={{ width: 'fit-content', mb: 2, justifyContent: 'center' }}>
                                    This member does not have an active nutrition plan. Please create a new plan by adding meals and saving.
                                </Alert>
                            )}
                            <PlanHeader member={member} />

                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3, mb: 2 }}>
                                <Tabs
                                    value={selectedTab}
                                    onChange={(event, newValue) => setSelectedTab(newValue)}
                                    variant="fullWidth"
                                    sx={{
                                        '& .MuiTabs-flexContainer': {
                                            flexWrap: 'wrap',
                                        },
                                    }}
                                >
                                    {Array.from({ length: activePlan?.cycleLength ?? activePlan?.planSnapshot?.days?.length ?? 1 }, (_, i) => (
                                        <Tab key={i} label={`Day ${i + 1}`} id={`tab-${i}`} aria-controls={`tabpanel-${i}`} />
                                    ))}
                                </Tabs>
                            </Box>
                            {Array.from({ length: activePlan?.cycleLength ?? 1 }, (_, index) => (
                                <div key={index} role="tabpanel" id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} hidden={selectedTab !== index}>
                                    {selectedTab === index && <NutritionPlanDay dayIndex={index} />}
                                </div>
                            ))}
                            <PlanBuilderActionButtons onClose={handleClose} onSave={handleSave} />
                            {showMealItemForm &&
                                <MealItemForm mealItem={undefined} editable={true} onClose={() => setShowMealItemForm(false)} onSubmit={handleMealItemSubmit} />
                            }
                        </>
                    ) : (
                        <Alert severity="error" variant="filled">
                            Member not selected or you are not authorized to view this member's nutrition plan.
                        </Alert>
                    )}
                </>
            )}
        </div>
    )
}

export default NutritionPlanBuilder;